import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import type { User, Session, AuthError, AuthChangeEvent } from '@supabase/supabase-js';
import { supabase, clearStoredSupabaseSession } from '@/lib/supabase';
import { getAuthRedirectUrl, APP_BASE_URL } from '@/lib/capacitor';
import { useAuthStore } from '@/store/authStore';
import { clearLimitsCache } from '@/hooks/useLimits';
import { pushNotificationService } from '@/features/notifications/services/pushNotifications';
import { CONSENT_VERSIONS, DEFAULT_TIMEZONE } from '../consent';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signUp: (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    acceptTerms?: boolean,
    acceptHealthDataConsent?: boolean,
    redirectPath?: string
  ) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<{ error: AuthError | null }>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: AuthError | null }>;
  resendVerificationEmail: (email: string) => Promise<{ error: AuthError | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { setUser: setStoreUser, logout: logoutStore } = useAuthStore();

  // Clear all auth state
  const clearAuthState = () => {
    setUser(null);
    setSession(null);
    setStoreUser(null);
    logoutStore();
    // Der Limit-Cache lebt als Modul-Variable und wuerde den Logout sonst
    // ueberleben - der naechste User erbt dann fremde Limits.
    clearLimitsCache();
    setIsLoading(false);
  };

  useEffect(() => {
    // URL token consumption is owned by the auth pages
    // (AuthConfirmPage / AuthCallbackPage / ResetPasswordConfirmPage) plus the
    // Supabase client's own `detectSessionInUrl`. AuthContext only observes
    // session state via getSession + onAuthStateChange.
    supabase.auth
      .getSession()
      .then(({ data: { session }, error }) => {
        if (error) {
          console.warn('Error getting session, clearing local auth:', error.message);
          clearStoredSupabaseSession();
          clearAuthState();
          return;
        }

        setSession(session);
        setUser(session?.user ?? null);
        setStoreUser(session?.user ?? null);
        setIsLoading(false);
      })
      .catch((err) => {
        console.warn('Unexpected error in getSession, clearing local auth:', err);
        clearStoredSupabaseSession();
        clearAuthState();
      });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event: AuthChangeEvent, session) => {
      console.log('Auth event:', event);

      // Handle token refresh failure
      if (event === 'TOKEN_REFRESHED' && !session) {
        console.warn('Token refresh failed, signing out');
        clearStoredSupabaseSession();
        clearAuthState();
        return;
      }

      // Handle sign out or session expiry
      if (event === 'SIGNED_OUT' || !session) {
        clearAuthState();
        return;
      }

      // Identitaet oder Profil hat gewechselt: Limits neu ermitteln
      if (event === 'SIGNED_IN' || event === 'USER_UPDATED') {
        clearLimitsCache();
      }

      setSession(session);
      setUser(session?.user ?? null);
      setStoreUser(session?.user ?? null);
      setIsLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [setStoreUser, logoutStore]);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signUp = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    acceptTerms?: boolean,
    acceptHealthDataConsent?: boolean,
    redirectPath?: string
  ) => {
    const displayName = `${firstName} ${lastName}`.trim();

    // Build the confirm URL with optional next-after-confirm path.
    // Email templates produce `${SiteURL}/auth/confirm?token_hash=...&type=signup`,
    // so we mirror that target here for emailRedirectTo (used when Supabase
    // falls back to `{{ .ConfirmationURL }}` style links).
    let callbackUrl = '/auth/confirm?type=signup';
    if (redirectPath) {
      callbackUrl += `&next=${encodeURIComponent(redirectPath)}`;
    }

    // Make sure no stale (and possibly invalid) session is attached to the
    // outgoing signUp request. If a previous session's refresh token was
    // revoked the SDK would still send an `Authorization: Bearer <stale>`
    // header, which causes the auth server to fail the signup with a 500.
    // We sign out locally (no network round-trip) and wipe the persisted
    // session before issuing the signUp call.
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch (err) {
      console.warn('Local signOut before signUp failed:', err);
    }
    clearStoredSupabaseSession();
    clearAuthState();

    // Auto-detect timezone from device
    const detectedTimezone =
      Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIMEZONE;

    // Alle Profilfelder gehen als User-Metadata mit. Der Trigger
    // handle_new_user (Migration 169) liest sie beim INSERT auf auth.users
    // aus und legt damit die Profilzeile vollstaendig an.
    //
    // Bewusst KEIN zusaetzlicher upsert auf profiles: bei aktivierter
    // E-Mail-Bestaetigung liefert signUp() keine Session, der Aufruf liefe
    // als anon und scheiterte an der RLS (profiles_insert_own gilt nur fuer
    // authenticated). Genau daran sind bisher saemtliche Einwilligungen
    // verloren gegangen.
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          display_name: displayName,
          first_name: firstName,
          last_name: lastName,
          timezone: detectedTimezone,
          accept_terms: acceptTerms === true,
          accept_health_data_consent: acceptHealthDataConsent === true,
          privacy_version: CONSENT_VERSIONS.privacy,
          terms_version: CONSENT_VERSIONS.terms,
          health_data_consent_version: CONSENT_VERSIONS.healthData,
        },
        emailRedirectTo: getAuthRedirectUrl(callbackUrl),
      },
    });

    return { error };
  };

  const signOut = async () => {
    try {
      // Deactivate all push tokens before signing out so the RPC can
      // still identify the user via the active session.
      await pushNotificationService.deactivateAll();
      await pushNotificationService.cleanup();

      const { error } = await supabase.auth.signOut();
      if (!error) {
        clearAuthState();
      }
      return { error };
    } catch (error) {
      console.error('Error signing out:', error);
      clearAuthState();
      return { error: error as AuthError };
    }
  };

  const resetPassword = async (email: string) => {
    // Always use web URL for password reset emails
    // This ensures the link works regardless of where the email is opened
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${APP_BASE_URL}/reset-password/confirm`,
    });
    return { error };
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    return { error };
  };

  const resendVerificationEmail = async (email: string) => {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
      options: {
        emailRedirectTo: getAuthRedirectUrl('/auth/confirm?type=signup'),
      },
    });
    return { error };
  };

  const value: AuthContextType = {
    user,
    session,
    isLoading,
    isAuthenticated: !!user,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
    resendVerificationEmail,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
